import {
  type ConvertedPiece,
  isUncertain,
  writeSyllable,
} from "@kensio/pinyinjs";

import type { WrittenSyllable } from "./copy-html.js";
import { element } from "./dom.js";
import { toneClass } from "./tone-colour.js";
import { renderTranscriptions } from "./transcription-panels.js";

/**
 * Turning a conversion into what the visitor sees.
 *
 * The clipboard half is `copy-html.ts`, and both are built from the same
 * `ConvertedPiece[]` — the pieces are the decomposition of one answer rather
 * than two answers produced separately, so the colours on the clipboard are by
 * construction the colours on the screen.
 */

/** The class marking a reading the decode was not sure about. */
const uncertainClass = "cb-uncertain";

/**
 * The readings a piece was chosen over, written out.
 *
 * Only present on a piece the decode is actually unsure of. Shown as a tooltip
 * rather than a footnote, because for a learner it is a curiosity — "this
 * character has another reading" — and for a teacher checking a worksheet it
 * is the one thing worth a second look.
 */
function alternativesOf(piece: ConvertedPiece): string | undefined {
  const confidence = piece.confidence;
  if (confidence === undefined || !isUncertain(confidence)) {
    return undefined;
  }

  const written = [
    ...new Set(
      confidence.alternatives.map((alternative) =>
        alternative.reading.map((syllable) => writeSyllable(syllable)).join(""),
      ),
    ),
  ];

  return written.length === 0
    ? undefined
    : `Could also be read ${written.join(" or ")}`;
}

/**
 * One piece as a node.
 *
 * A piece with no syllable is the text between syllables — spaces, the
 * punctuation, anything that was not Han — and becomes a bare text node rather
 * than a `<span>`. There is nothing to colour and nothing to say about it, and
 * leaving it unwrapped is what makes a selection of the output copy cleanly
 * when somebody drags across it instead of using the button.
 */
export function pieceNode(piece: ConvertedPiece): Node {
  if (piece.syllable === undefined) {
    return document.createTextNode(piece.text);
  }

  const alternatives = alternativesOf(piece);
  const classes = [
    toneClass(piece.syllable.tone),
    alternatives === undefined ? undefined : uncertainClass,
  ]
    .filter((name) => name !== undefined)
    .join(" ");

  return element("span", {
    text: piece.text,
    ...(classes !== "" && { className: classes }),
    attributes: { title: alternatives },
  });
}

/**
 * A whole conversion as nodes, ready to go into the output panel.
 */
export function outputNodes(
  pieces: readonly ConvertedPiece[],
): readonly Node[] {
  return pieces.map((piece) => pieceNode(piece));
}

/**
 * One coloured syllable, or a run of text that is not one.
 *
 * The same shape as {@link pieceNode} produces from a conversion, so the
 * pinyin panel looks the same on every tool whether a dictionary was involved
 * in producing it or not.
 */
export function writtenNode(written: WrittenSyllable): Node {
  const tone = toneClass(written.tone);

  return tone === undefined
    ? document.createTextNode(written.text)
    : element("span", { text: written.text, className: tone });
}

/**
 * Put a conversion on the page: the pinyin, and every other system beside it.
 *
 * One call, because they are one answer. A tool that rendered the pinyin and
 * forgot the panels would leave the page showing two different conversions.
 */
export function showConversion(
  output: HTMLElement,
  panels: readonly HTMLElement[],
  pieces: readonly ConvertedPiece[],
): void {
  output.replaceChildren(...outputNodes(pieces));
  renderTranscriptions(panels, pieces);
}
